import {
  addHours,
  differenceInHours,
  differenceInMonths,
  getMonth,
  getYear,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { TaxFrequency, TaxTypeEnum } from '__generated__/GQL';
import webView from 'common/webView';

import { TaxForecastRate } from '../hooks/useTaxForecastPeriod';
import locales from '../locales';
import { Quarter } from '../types';

export const parseTaxForecastPeriodInterval = (
  startDate: number,
  endDate: number,
): TaxFrequency => {
  const monthDifference = differenceInMonths(endDate, startDate);

  /**
   * We must check like this as older period have exactly a difference of 1 month,
   * but newly generated ones end 1 second before a full month
   */
  if (monthDifference <= 1) {
    return TaxFrequency.Monthly;
  } else {
    return TaxFrequency.Quarterly;
  }
};

const getOffsetFromParisTimezone = (localDate: number | Date): number => {
  const utcDate = zonedTimeToUtc(
    localDate,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const parisDate = utcToZonedTime(utcDate, 'Europe/Paris');

  return differenceInHours(parisDate, localDate);
};

export const formatPeriodDate = (
  startDate: number,
  endDate: number,
): string => {
  /**
   * Since the server returns the timestamps based on the Paris timezone,
   * we need to add an offset to it in case the user's local timezone is behind it.
   *
   * Otherwise we'll show the previous period's label instead of the correct one.
   */
  const offsetFromParisTimezone = getOffsetFromParisTimezone(startDate);
  const parisTimezoneStartDate = addHours(startDate, offsetFromParisTimezone);
  const interval = parseTaxForecastPeriodInterval(startDate, endDate);

  if (interval === TaxFrequency.Monthly) {
    return new Intl.DateTimeFormat(locales.date.format, {
      month: 'long',
      year: 'numeric',
    }).format(parisTimezoneStartDate);
  }

  const i = Math.floor(getMonth(parisTimezoneStartDate) / 3);
  const quarter = Object.values(Quarter)[i] as Quarter;

  return locales
    .formatString(locales.date.quarter[quarter], {
      year: getYear(parisTimezoneStartDate),
    })
    .toString();
};

export const getRatesAmount = (taxForecasts: TaxForecastRate[]) => {
  const ratesAmount: Record<TaxTypeEnum, number> = {
    CFP: 0,
    FRAIS_CHAMBRE: 0,
    PRELEV_LIB: 0,
    RSI: 0,
  };

  taxForecasts.forEach((forecast) => {
    ratesAmount[forecast.taxType] = forecast.amount;
  });

  return ratesAmount;
};

export const getMobileAppLang = (): string => {
  // Keep the same lang than the mobile app in the webview
  return webView?.config?.lang || navigator.language;
};
